import { makeAutoObservable, runInAction } from 'mobx';
import type { WeatherConditionRule } from '~/services/scheduler/types';
import { matchesWeather } from '~/services/scheduler/utils/weatherConditionMatcher';
import { normWeatherCondition } from '~/api/transforms/mediaSync';
import type { WeatherConditionDto } from '~/api/types/sync';
import { weatherStore } from './weather.store';

export type ElementoTipo = 'BUTTON' | 'QRCODE' | 'WEATHER' | 'MEDIA_OVERLAY';
export type ButtonActionType =
  | 'SKIP_TO_NEXT'
  | 'DISMISS_ELEMENTS'
  | 'FIRE_TRIGGER_ONCE'
  | 'FIRE_TRIGGER_LOOP'
  // Broadcast: dispara o gatilho em TODOS os players configurados (via server),
  // não só neste device. Vide actionDispatcher.fireTriggerAllDevices.
  | 'FIRE_TRIGGER_ALL_DEVICES';
export type QrEccLevel = 'L' | 'M' | 'Q' | 'H';
export type MediaFitMode = 'contain' | 'cover' | 'fill';
export type MediaOverlayType = 'image' | 'video';

export interface InteractiveElement {
  id: string;
  contentId: string;
  tipo: ElementoTipo;
  sortOrder: number;
  labelInterno: string;
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
  inSeconds: number;
  outSeconds: number | null;
  label: string;
  bgColor: string;
  textColor: string;
  fgColor: string;
  opacity: number;
  actionType: ButtonActionType | null;
  actionPayload: Record<string, unknown> | null;
  qrUrl: string;
  qrTrackingToken: string | null;
  qrEccLevel: QrEccLevel;
  qrRenderUrl: string | null;
  // MEDIA_OVERLAY — conteúdo (imagem/vídeo) sobreposto. `mediaUrl` já vem
  // presigned do server; `mediaType` decide <img> vs <video>.
  mediaContentId: string | null;
  mediaFitMode: MediaFitMode;
  mediaUrl: string | null;
  mediaType: MediaOverlayType | null;
  mediaMimeType: string | null;
  /**
   * V088 (2026-08-08) — regra de clima do CONTEÚDO SOBREPOSTO
   * (só p/ MEDIA_OVERLAY). Cliente aplica {@code matchesWeather} antes de
   * renderizar. `null`/undefined = sem restrição. Vide
   * regras-temporais-player.md R2/R4.
   */
  mediaWeatherCondition: WeatherConditionRule | null;
  active: boolean;
}

export interface ElementSyncDto {
  id: string;
  content_id: string;
  tipo: string;
  sort_order: number;
  label_interno: string;
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  in_seconds: number;
  out_seconds: number | null;
  label: string;
  bg_color: string;
  text_color: string;
  fg_color: string;
  opacity: number;
  action_type: string | null;
  action_payload: Record<string, unknown> | null;
  qr_url: string;
  qr_tracking_token: string | null;
  qr_ecc_level: string;
  qr_render_url: string | null;
  media_content_id?: string | null;
  media_fit_mode?: string | null;
  media_url?: string | null;
  media_type?: string | null;
  media_mime_type?: string | null;
  // V088 — regra de clima do conteúdo sobreposto (só MEDIA_OVERLAY).
  media_weather_condition?: unknown;
  active: boolean;
}

function normalizeElement(dto: ElementSyncDto): InteractiveElement {
  return {
    id: dto.id,
    contentId: dto.content_id,
    tipo: dto.tipo as ElementoTipo,
    sortOrder: dto.sort_order,
    labelInterno: dto.label_interno ?? '',
    xPct: dto.x_pct,
    yPct: dto.y_pct,
    wPct: dto.w_pct,
    hPct: dto.h_pct,
    inSeconds: dto.in_seconds,
    outSeconds: dto.out_seconds,
    label: dto.label ?? '',
    bgColor: dto.bg_color ?? '#667eea',
    textColor: dto.text_color ?? '#FFFFFF',
    fgColor: dto.fg_color ?? '#000000',
    opacity: dto.opacity ?? 1,
    actionType: dto.action_type as ButtonActionType | null,
    actionPayload: dto.action_payload,
    qrUrl: dto.qr_url ?? '',
    qrTrackingToken: dto.qr_tracking_token,
    qrEccLevel: (dto.qr_ecc_level ?? 'M') as QrEccLevel,
    qrRenderUrl: dto.qr_render_url,
    mediaContentId: dto.media_content_id ?? null,
    mediaFitMode: (dto.media_fit_mode ?? 'fill') as MediaFitMode,
    mediaUrl: dto.media_url ?? null,
    mediaType: (dto.media_type ?? null) as MediaOverlayType | null,
    mediaMimeType: dto.media_mime_type ?? null,
    mediaWeatherCondition: normWeatherCondition(
      dto.media_weather_condition as WeatherConditionDto | null | undefined,
    ),
    active: dto.active,
  };
}

/**
 * Assinatura ESTÁVEL de uma lista de elementos — usada pra detectar mudança
 * real entre syncs. Exclui `media_url` (presigned volátil que muda a cada sync
 * sem mudança de conteúdo — espelha o `stableElementosHashCode` do server).
 */
function stableElementsSig(els: ElementSyncDto[] | undefined): string {
  if (!els || els.length === 0) return '';
  return JSON.stringify(
    els.map((el) => {
      const copy: Record<string, unknown> = { ...el };
      delete copy.media_url;
      return copy;
    }),
  );
}

class InteractiveElementsStore {
  elements: InteractiveElement[] = [];
  currentContentId: string | null = null;
  currentTimeSeconds = 0;
  dismissed = false;
  playVersion = 0;
  private elementsRegistry: Map<string, ElementSyncDto[]> = new Map();

  constructor() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    makeAutoObservable(this, { elementsRegistry: false } as any);
  }

  registerAllElements(
    entries: Array<{ contentId: string; elementos: ElementSyncDto[] }>,
  ): void {
    // Captura a assinatura dos elementos do conteúdo tocando AGORA antes de
    // substituir o registry — pra detectar mudança ao vivo (vide abaixo).
    const playingId = this.currentContentId;
    const prevSig =
      playingId !== null
        ? stableElementsSig(this.elementsRegistry.get(playingId))
        : null;

    this.elementsRegistry.clear();
    for (const entry of entries) {
      if (entry.elementos.length > 0) {
        this.elementsRegistry.set(entry.contentId, entry.elementos);
      }
    }

    // Live-refresh: se os elementos do conteúdo ATUALMENTE tocando mudaram
    // neste sync (operador editou botão/QR/clima/overlay no admin → SSE
    // content_changed → sync), re-aplica na hora — sem esperar o vídeo dar
    // loop ou trocar de conteúdo. Sem isso, a edição só aparecia no F5/loop.
    // Compara assinatura ESTÁVEL (sem media_url presigned) pra NÃO resetar a
    // janela de tempo do overlay a cada poll quando nada mudou.
    if (playingId !== null) {
      const nextSig = stableElementsSig(this.elementsRegistry.get(playingId));
      if (nextSig !== prevSig) {
        this.setElementsForContent(playingId);
      }
    }
  }

  get activeButtons(): InteractiveElement[] {
    if (this.dismissed) return [];
    return this.elements.filter(
      (e) => e.tipo === 'BUTTON' && e.active && this.isInTimeWindow(e),
    );
  }

  get activeQRCodes(): InteractiveElement[] {
    if (this.dismissed) return [];
    return this.elements.filter(
      (e) => e.tipo === 'QRCODE' && e.active && this.isInTimeWindow(e),
    );
  }

  get allButtons(): InteractiveElement[] {
    if (this.dismissed) return [];
    return this.elements.filter((e) => e.tipo === 'BUTTON' && e.active);
  }

  get allQRCodes(): InteractiveElement[] {
    if (this.dismissed) return [];
    return this.elements.filter((e) => e.tipo === 'QRCODE' && e.active);
  }

  /** Elementos de clima (display-only). Renderizam em todas as plataformas. */
  get allWeather(): InteractiveElement[] {
    if (this.dismissed) return [];
    return this.elements.filter((e) => e.tipo === 'WEATHER' && e.active);
  }

  /**
   * Conteúdos sobrepostos (imagem/vídeo). Display-only — renderizam em TODAS
   * as plataformas (como Clima/QR). Exige `mediaUrl` (server presigna no sync);
   * sem URL, o item é silenciosamente ignorado (best-effort gracioso).
   *
   * V088 (2026-08-08) — respeita `mediaWeatherCondition` do CONTEÚDO
   * SOBREPOSTO. Antes desta versão, MEDIA_OVERLAY renderizava fora do pipeline
   * de filtro de conteúdo → item com regra de clima aparecia mesmo fora da
   * regra. Regra vazia = passa; regra setada + sem dado de clima = conservador
   * (não renderiza). Vide regras-temporais-player.md R2/R4.
   */
  get allMediaOverlays(): InteractiveElement[] {
    if (this.dismissed) return [];
    return this.elements.filter((e) => {
      if (e.tipo !== 'MEDIA_OVERLAY') return false;
      if (!e.active) return false;
      if (!e.mediaUrl) return false;
      if (e.mediaWeatherCondition) {
        return matchesWeather(e.mediaWeatherCondition, weatherStore.current);
      }
      return true;
    });
  }

  get hasActiveElements(): boolean {
    return this.activeButtons.length > 0 || this.activeQRCodes.length > 0;
  }

  isElementVisible(el: InteractiveElement): boolean {
    return this.isInTimeWindow(el);
  }

  setElementsForContent(
    contentId: string,
    dtos?: ElementSyncDto[] | undefined | null,
  ): void {
    const source = dtos ?? this.elementsRegistry.get(contentId);
    const next = Array.isArray(source) ? source.map(normalizeElement) : [];
    runInAction(() => {
      this.elements = next;
      this.currentContentId = contentId;
      this.currentTimeSeconds = 0;
      this.dismissed = false;
      this.playVersion++;
    });
  }

  clearElements(): void {
    this.elements = [];
    this.currentContentId = null;
    this.currentTimeSeconds = 0;
    this.dismissed = false;
    this.playVersion = 0;
  }

  updateTime(seconds: number): void {
    const rounded = Math.floor(seconds * 2) / 2;
    if (rounded !== this.currentTimeSeconds) {
      this.currentTimeSeconds = rounded;
    }
  }

  dismissAll(): void {
    this.dismissed = true;
  }

  private isInTimeWindow(el: InteractiveElement): boolean {
    if (this.currentTimeSeconds < el.inSeconds) return false;
    if (el.outSeconds !== null && this.currentTimeSeconds >= el.outSeconds) {
      return false;
    }
    return true;
  }
}

export const interactiveElementsStore = new InteractiveElementsStore();
export default InteractiveElementsStore;
